import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Check, Eye, Trash, ChatCircleDots, CaretDown } from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const C = {
  bg: '#0a0a0a', surface: '#141414',
  border: 'rgba(255,255,255,0.06)', borderHi: 'rgba(255,255,255,0.12)',
  borderLime: 'rgba(200,241,53,0.32)',
  lime: '#c8f135', limeText: '#04050a',
  text: '#f0f0e8', dim: 'rgba(240,240,232,0.65)', faint: 'rgba(240,240,232,0.35)',
};
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const DISP = "'Syne', system-ui, sans-serif";

const Kicker = ({ children, lime }) => (
  <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.22em',
    color: lime ? C.lime : C.faint, textTransform: 'uppercase',
    fontWeight: 700, marginBottom: 10 }}>{children}</p>
);

/* ── Instant Mode: 2 mode cards + 1 optional input ── */
const InstantForm = ({ onGenerate }) => {
  const [mode, setMode] = useState('whole-business');
  const [topic, setTopic] = useState('');
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);

  const submit = () => {
    const payload = {
      topic_focus: mode === 'topic' ? topic.trim() : '',
      context_notes: notes.trim(),
    };
    if (mode === 'topic' && payload.topic_focus.length < 3) {
      toast.error('Tell us the topic to cover.'); return;
    }
    onGenerate(payload);
  };

  return (
    <div data-testid="faq-instant-form">
      <div style={{ textAlign: 'center', marginBottom: 'clamp(32px, 5vw, 52px)' }}>
        <Kicker lime>FAQ page · 3 credits</Kicker>
        <h1 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(28px, 5vw, 50px)', letterSpacing: '-0.035em', color: C.text, lineHeight: 1.05, marginBottom: 14 }}>
          The questions, answered.
        </h1>
        <p style={{ fontFamily: MONO, fontSize: 13.5, color: C.dim, lineHeight: 1.8, maxWidth: 540, margin: '0 auto' }}>
          We&apos;ll write 8 to 12 search-optimised questions and real answers in your voice.
        </p>
      </div>

      <p style={{ fontFamily: MONO, fontSize: 10, color: C.faint, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 12, fontWeight: 700 }}>
        Scope
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { id: 'whole-business', label: 'About my whole business', desc: 'The default. Operations, trust, products, outcomes.', testid: 'faq-mode-whole' },
          { id: 'topic', label: 'About a specific topic', desc: 'Pricing, refunds, onboarding — name the focus.', testid: 'faq-mode-topic' },
        ].map((opt) => (
          <button key={opt.id} onClick={() => setMode(opt.id)} data-testid={opt.testid}
            style={{
              textAlign: 'left',
              background: mode === opt.id ? 'rgba(200,241,53,0.04)' : C.surface,
              border: `1px solid ${mode === opt.id ? C.borderLime : C.border}`,
              borderRadius: 14, padding: '18px 20px', cursor: 'pointer',
              transition: 'border-color 0.2s, background 0.2s',
            }}>
            <p style={{ fontFamily: DISP, fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 5 }}>{opt.label}</p>
            <p style={{ fontFamily: MONO, fontSize: 11.5, color: C.dim, lineHeight: 1.6 }}>{opt.desc}</p>
          </button>
        ))}
      </div>

      {mode === 'topic' && (
        <div style={{ marginBottom: 24 }}>
          <p style={{ fontFamily: MONO, fontSize: 10, color: C.faint, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 10, fontWeight: 700 }}>
            What&apos;s the focus?
          </p>
          <input type="text" value={topic} onChange={(e) => setTopic(e.target.value)}
            data-testid="faq-topic-input"
            placeholder='e.g. "shipping and returns" or "onboarding new customers"'
            style={{
              width: '100%', background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`,
              color: C.text, padding: '14px 16px', borderRadius: 10,
              fontFamily: MONO, fontSize: 14.5, outline: 'none',
            }}
          />
        </div>
      )}

      {!showNotes ? (
        <button onClick={() => setShowNotes(true)} data-testid="faq-add-context-btn"
          style={{ background: 'transparent', color: C.dim, border: 'none', cursor: 'pointer',
            fontFamily: MONO, fontSize: 11.5, letterSpacing: '0.06em',
            padding: '4px 0', marginBottom: 22, textDecoration: 'underline', textUnderlineOffset: 4,
            textDecorationColor: 'rgba(240,240,232,0.15)' }}>
          + Add policies, prices, or specific details (optional)
        </button>
      ) : (
        <div style={{ marginBottom: 22 }}>
          <p style={{ fontFamily: MONO, fontSize: 10, color: C.faint, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 10, fontWeight: 700 }}>
            Anything specific to mention?
          </p>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
            data-testid="faq-notes-input"
            placeholder="Shipping cost, return window, response time, pricing tiers… all optional."
            style={{
              width: '100%', background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`,
              color: C.text, padding: '14px 16px', borderRadius: 10,
              fontFamily: MONO, fontSize: 13.5, outline: 'none', resize: 'vertical', lineHeight: 1.6,
            }}
          />
        </div>
      )}

      <button onClick={submit} data-testid="faq-generate-btn"
        style={{ background: C.lime, color: C.limeText, border: 'none', cursor: 'pointer',
          padding: '14px 28px', borderRadius: 10, width: '100%',
          fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        Write the FAQs →
      </button>
    </div>
  );
};

const STEPS = [
  'Finding the right questions',
  'Writing answers in your voice',
  'Optimising for search',
  'Done',
];

const GenerationProgress = ({ done }) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (done) { setTick(STEPS.length); return; }
    const t = setInterval(() => setTick((n) => Math.min(STEPS.length - 1, n + 1)), 1900);
    return () => clearInterval(t);
  }, [done]);
  return (
    <div data-testid="faq-progress" style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center', padding: '60px 0' }}>
      <Kicker lime>Writing</Kicker>
      <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(26px, 4vw, 38px)', letterSpacing: '-0.025em', color: C.text, lineHeight: 1.1, marginBottom: 36 }}>
        Building your FAQ page…
      </h2>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left' }}>
        {STEPS.map((s, i) => {
          const isDone = i < tick;
          const isCurrent = i === tick && !done;
          return (
            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 12,
              padding: '11px 14px', marginBottom: 6, borderRadius: 8,
              background: isCurrent ? 'rgba(200,241,53,0.05)' : 'transparent',
              opacity: isDone || isCurrent ? 1 : 0.4 }}>
              <span style={{ width: 18, height: 18, borderRadius: '50%',
                background: isDone ? C.lime : 'transparent',
                border: isDone ? 'none' : `1.5px solid ${isCurrent ? C.lime : C.borderHi}`,
                display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                {isDone && <Check size={11} weight="bold" color={C.limeText} />}
              </span>
              <span style={{ fontFamily: MONO, fontSize: 13.5, color: isDone || isCurrent ? C.text : C.faint }}>{s}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/* ── FAQ output: accordion-style preview that mirrors the public page ── */
const FaqAccordion = ({ faqs }) => {
  const [open, setOpen] = useState(0);
  return (
    <div data-testid="faq-output-list" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {(faqs || []).map((f, i) => {
        const isOpen = open === i;
        return (
          <div key={i} data-testid={`faq-item-${i}`}
            style={{ background: isOpen ? 'rgba(200,241,53,0.03)' : 'rgba(255,255,255,0.025)',
              border: `1px solid ${isOpen ? C.borderLime : C.border}`,
              borderRadius: 14, transition: 'all 0.25s', overflow: 'hidden' }}>
            <button onClick={() => setOpen(isOpen ? -1 : i)}
              style={{ width: '100%', background: 'transparent', border: 'none', cursor: 'pointer',
                padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left' }}>
              <span style={{ fontFamily: MONO, fontSize: 11, color: C.faint, letterSpacing: '0.08em', fontWeight: 700, flexShrink: 0 }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <span style={{ flex: 1, fontFamily: DISP, fontWeight: 700, fontSize: 16, color: C.text, letterSpacing: '-0.012em', lineHeight: 1.3 }}>
                {f.q}
              </span>
              <CaretDown size={16} weight="bold" color={C.lime}
                style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s', flexShrink: 0 }} />
            </button>
            {isOpen && (
              <div style={{ padding: '0 22px 22px 55px', fontFamily: "'Lora', Georgia, serif", fontSize: 15, lineHeight: 1.78, color: 'rgba(240,240,232,0.8)' }}>
                {f.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const Output = ({ post, onUpdate, onBack }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const faqs = post.sections?.faqs || [];

  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/blog-post/${post.id}/publish`);
      onUpdate(data.post);
      toast.success('Published — your FAQ page is live.');
      if (data.public_url) window.open(data.public_url, '_blank', 'noopener');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Publish failed';
      if (typeof detail === 'string' && detail.toLowerCase().includes('public blog is not enabled')) {
        toast.error(detail, { action: { label: 'Open Settings', onClick: () => navigate('/dashboard/settings#public-blog') } });
      } else { toast.error(detail); }
    } finally { setBusy(false); }
  };
  const unpublish = async () => {
    if (!window.confirm('Take this FAQ page offline?')) return;
    setBusy(true);
    try { const { data } = await axios.post(`${API_URL}/api/blog-post/${post.id}/unpublish`); onUpdate(data.post); toast.success('Unpublished'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Unpublish failed'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this FAQ page? This cannot be undone.')) return;
    try { await axios.delete(`${API_URL}/api/blog-post/${post.id}`); toast.success('Deleted'); navigate('/dashboard/assets?tab=blog'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="faq-output">
      <button onClick={onBack} data-testid="faq-back-btn"
        style={{ background: 'transparent', border: 'none', color: C.dim, cursor: 'pointer',
          fontFamily: MONO, fontSize: 11, letterSpacing: '0.08em',
          display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 22 }}>
        <ArrowLeft size={13} /> Back
      </button>

      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999,
        background: 'rgba(200,241,53,0.08)', border: `1px solid ${C.borderLime}`, marginBottom: 18 }}>
        <ChatCircleDots size={13} weight="duotone" color={C.lime} />
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.lime, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700 }}>
          {faqs.length} questions
        </span>
      </div>

      <h1 data-testid="faq-title" style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(26px, 4vw, 38px)', letterSpacing: '-0.025em', color: C.text, lineHeight: 1.1, marginBottom: 14 }}>
        {post.title}
      </h1>

      <p style={{ fontFamily: MONO, fontSize: 14, color: C.dim, lineHeight: 1.8, marginBottom: 30 }}>
        {post.meta_description}
      </p>

      <FaqAccordion faqs={faqs} />

      {post.quality_warnings && post.quality_warnings.length > 0 && (
        <div style={{ marginTop: 28, padding: '14px 18px', borderRadius: 12, background: 'rgba(252,211,77,0.06)', border: '1px solid rgba(252,211,77,0.28)' }}>
          <p style={{ fontFamily: MONO, fontSize: 10, color: '#fcd34d', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>
            Quality nudge
          </p>
          {post.quality_warnings.map((w, i) => (
            <p key={i} style={{ fontFamily: MONO, fontSize: 12, color: 'rgba(252,211,77,0.85)', lineHeight: 1.6 }}>{w}</p>
          ))}
        </div>
      )}

      <div style={{ position: 'sticky', bottom: 0, marginTop: 36, padding: '16px 0',
        background: 'linear-gradient(180deg, transparent 0%, #0a0a0a 30%)',
        display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <span style={{ flex: 1 }} />
        {post.status === 'published' ? (
          <>
            <a href={post.public_url} target="_blank" rel="noopener noreferrer" data-testid="faq-view-live-link"
              style={{ background: 'transparent', color: C.lime, border: `1px solid ${C.borderLime}`,
                padding: '12px 22px', borderRadius: 10, textDecoration: 'none',
                fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Eye size={13} weight="bold" /> View live →
            </a>
            <button onClick={unpublish} disabled={busy} data-testid="faq-unpublish-btn"
              style={{ background: 'transparent', color: '#f87171', border: '1px solid rgba(248,113,113,0.32)', cursor: busy ? 'wait' : 'pointer',
                padding: '12px 22px', borderRadius: 10,
                fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Unpublish</button>
          </>
        ) : (
          <button onClick={publish} disabled={busy} data-testid="faq-publish-btn"
            style={{ background: C.lime, color: C.limeText, border: 'none', cursor: busy ? 'wait' : 'pointer',
              padding: '12px 22px', borderRadius: 10,
              fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            <Eye size={13} weight="bold" style={{ display: 'inline-block', marginRight: 6, verticalAlign: '-2px' }} />
            {busy ? 'Publishing…' : 'Publish →'}
          </button>
        )}
        <button onClick={remove} data-testid="faq-delete-btn"
          style={{ background: 'transparent', color: '#f87171', border: '1px solid rgba(248,113,113,0.32)',
            padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
            fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          <Trash size={13} weight="bold" />
        </button>
      </div>
    </div>
  );
};

export default function FaqCreatorPage() {
  const [phase, setPhase] = useState('input');
  const [post, setPost] = useState(null);
  const [done, setDone] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const postId = searchParams.get('id');
    if (!postId) return;
    (async () => {
      try { const { data } = await axios.get(`${API_URL}/api/blog-post/${postId}`); setPost(data.post); setPhase('output'); }
      catch { /* */ }
    })();
  }, [searchParams]);

  const generate = async (payload) => {
    setDone(false);
    setPhase('generating');
    try {
      const { data } = await axios.post(`${API_URL}/api/faq/generate`, payload);
      setPost(data.post);
      setDone(true);
      setTimeout(() => setPhase('output'), 900);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
      setPhase('input');
    }
  };

  return (
    <DashboardLayout>
      <div data-testid="faq-creator" style={{
        background: C.bg, color: C.text, minHeight: '100vh',
        padding: 'clamp(36px, 5vw, 56px) clamp(20px, 4vw, 40px)',
      }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          {phase === 'input' && <InstantForm onGenerate={generate} />}
          {phase === 'generating' && <GenerationProgress done={done} />}
          {phase === 'output' && post && <Output post={post} onUpdate={setPost} onBack={() => setPhase('input')} />}
        </div>
      </div>
    </DashboardLayout>
  );
}
